import { Spacing } from '@persona-id/design-tokens';
import { useFormState } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  ReportTemplateVersionCustomList,
  ReportTemplateVersionCustomListRunPayload,
} from 'dashboard/models/report-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setReportTemplateVersion } from 'dashboard/redux/actions/report-template-version';
import Button, { ButtonProps } from 'design-system/components/Button';
import TextArea from 'design-system/components/TextArea';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface CustomListRunButtonProps {
  reportTemplateVersion: ReportTemplateVersionCustomList;
}

export const CustomListRunButton = ({
  reportTemplateVersion,
  ...buttonProps
}: CustomListRunButtonProps & ButtonProps) => {
  const { handleSubmit, control, register } = useFormSecurely<
    ReportTemplateVersionCustomListRunPayload['attributes']
  >({ mode: 'onChange' });

  const { isSubmitting, errors, isValid } = useFormState({
    control,
  });

  const dispatch = useDispatch();
  const [runModalOpen, { enable: showRunModal, disable: hideRunModal }] = useToggle(false);
  const { error, callback: handleRun } = useApiCallback(
    async (description?: string) => {
      const { data } = await SessionClientDashboard.postResourceAction<
        ReportTemplateVersionCustomList,
        ReportTemplateVersionCustomListRunPayload
      >(
        {
          id: reportTemplateVersion.id,
          type: ApiModel.ReportTemplateVersionCustomList,
          attributes: {
            description: description,
          },
        },
        'run',
      );
      dispatch(setReportTemplateVersion(data));
      dispatch(showMessage('Successfully started report run.', MessageType.Success));
    },
    [reportTemplateVersion],
  );
  const isRunning = reportTemplateVersion.attributes['run-status'] === 'running';

  useApiErrorHandler({
    error,
    onError: () => dispatch(showMessage('Failed to run, please try again.', MessageType.Failure)),
    onNetworkError: () =>
      dispatch(showMessage('Failed to run, please try again.', MessageType.Failure)),
  });

  return (
    <>
      <Button
        id='report-template-version-custom-list__run'
        {...buttonProps}
        onClick={showRunModal}
        disabled={
          buttonProps.disabled || isRunning || reportTemplateVersion.attributes['total-count'] === 0
        }
      >
        Run
      </Button>
      <ModalWrapper isOpen={runModalOpen} handleCloseModal={hideRunModal}>
        <ModalContainer>
          <ModalHeader onClose={hideRunModal}>Run reports</ModalHeader>
          <ModalDescription>
            This will begin re-screening {reportTemplateVersion.attributes['total-count']}{' '}
            {reportTemplateVersion.attributes['total-count'] === 1 ? 'report' : 'reports'} that have
            been previously run in the current environment against the latest custom list.
          </ModalDescription>
          <ModalBody>
            <FormControlLabel htmlFor='report-template-version-custom-list-modal__description'>
              Description (optional)
            </FormControlLabel>
            <TextArea
              id='report-template-version-custom-list-modal__description'
              {...register('description', {
                maxLength: {
                  value: 255,
                  message: 'Please limit description to 255 characters',
                },
              })}
              placeholder='Run description'
            />
            {errors['description'] && <FormError>{errors['description'].message}</FormError>}
            <RunModalButtonGroup>
              <Button
                id='report-template-version-custom-list-modal__cancel-button'
                variant='basic'
                width='auto'
                onClick={hideRunModal}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                id='report-template-version-custom-list-modal__submit-button'
                width='auto'
                type='submit'
                disabled={isSubmitting || !isValid}
                onClick={() => {
                  handleSubmit(async (data) => {
                    await handleRun(data.description);
                    hideRunModal();
                  })();
                }}
              >
                Run reports
              </Button>
            </RunModalButtonGroup>
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

const RunModalButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: ${Spacing[2]};
`;
